import React, { CSSProperties } from "react";
import { FieldArray, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import XGSFormSelect from "../../../../ui-components/form/select/xgsFormSelect";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import { QuickPickupItemPackageTypeOptions } from "../../../../app/data/pickup/models";
import { commonSelector } from "../../../../slices/common/commonSlice";
import { XGSSelectOption } from "../../../../app/data/common/models";
import { BolReviewFormValues, emptyBolReviewFormValues } from "../utils/bolReviewMappers";

const selectMenuProps = {
  controlled: true,
  menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
  menuPosition: "fixed" as const,
  styles: {
    menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 9999 }),
  },
};

const BolCommodityRows: React.FC = () => {
  const commonState = useSelector(commonSelector);
  const { values } = useFormikContext<BolReviewFormValues>();

  const classOptions = (commonState.freightClasses as XGSSelectOption[])?.length
    ? commonState.freightClasses
    : [];

  return (
    <div className="xgs-captured-bol__commodity">
      <FieldArray name="items">
        {({ push, remove }) => (
          <>
            <div className="xgs-captured-bol__commodity-header">
              <span>Commodity Information</span>
              <button
                type="button"
                className="xgs-captured-bol__add-item"
                onClick={() => push(emptyBolReviewFormValues().items[0])}
              >
                + Add Item
              </button>
            </div>
            <div className="xgs-captured-bol__table-wrap">
              <table className="xgs-captured-bol__table xgs-captured-bol__table--figma">
                <thead>
                  <tr>
                    <th>PKG Type</th>
                    <th>Units</th>
                    <th>Weight (lbs)</th>
                    <th>Class</th>
                    <th>Description</th>
                    <th>L (in)</th>
                    <th>W (in)</th>
                    <th>H (in)</th>
                    <th>Notes</th>
                    <th>Sidemark</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {values.items.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="xgs-captured-bol__table-empty">
                        No line items
                      </td>
                    </tr>
                  ) : (
                    values.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <XGSFormSelect
                            name={`items[${idx}].packageType`}
                            label=""
                            labelMode={LabelModes.column}
                            options={QuickPickupItemPackageTypeOptions}
                            isClearable
                            placeholder="Type"
                            className="xgs-captured-bol__table-cell"
                            {...selectMenuProps}
                          />
                        </td>
                        <td>
                          <XGSFormInput
                            type="number"
                            inputMode="numeric"
                            min={0}
                            step={1}
                            name={`items[${idx}].numberOfUnits`}
                            label=""
                            labelMode={LabelModes.column}
                            required
                            requiredAsteriskDisabled
                            className="xgs-captured-bol__table-cell"
                          />
                        </td>
                        <td>
                          <XGSFormInput
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="any"
                            name={`items[${idx}].weight`}
                            label=""
                            labelMode={LabelModes.column}
                            required
                            requiredAsteriskDisabled
                            className="xgs-captured-bol__table-cell"
                          />
                        </td>
                        <td>
                          <XGSFormSelect
                            name={`items[${idx}].ratingClass`}
                            label=""
                            labelMode={LabelModes.column}
                            options={classOptions}
                            isClearable
                            placeholder="Class"
                            className="xgs-captured-bol__table-cell"
                            {...selectMenuProps}
                          />
                        </td>
                        <td>
                          <XGSFormInput
                            type="text"
                            name={`items[${idx}].description`}
                            label=""
                            labelMode={LabelModes.column}
                            className="xgs-captured-bol__table-cell"
                          />
                        </td>
                        <td>
                          <XGSFormInput
                            type="number"
                            inputMode="numeric"
                            min={0}
                            step={1}
                            name={`items[${idx}].length`}
                            label=""
                            labelMode={LabelModes.column}
                            className="xgs-captured-bol__table-cell"
                          />
                        </td>
                        <td>
                          <XGSFormInput
                            type="number"
                            inputMode="numeric"
                            min={0}
                            step={1}
                            name={`items[${idx}].width`}
                            label=""
                            labelMode={LabelModes.column}
                            className="xgs-captured-bol__table-cell"
                          />
                        </td>
                        <td>
                          <XGSFormInput
                            type="number"
                            inputMode="numeric"
                            min={0}
                            step={1}
                            name={`items[${idx}].height`}
                            label=""
                            labelMode={LabelModes.column}
                            className="xgs-captured-bol__table-cell"
                          />
                        </td>
                        <td>
                          <XGSFormInput
                            type="text"
                            name={`items[${idx}].notes`}
                            label=""
                            labelMode={LabelModes.column}
                            className="xgs-captured-bol__table-cell"
                          />
                        </td>
                        <td>
                          <XGSFormInput
                            type="text"
                            name={`items[${idx}].sidemark`}
                            label=""
                            labelMode={LabelModes.column}
                            className="xgs-captured-bol__table-cell"
                          />
                        </td>
                        <td className="xgs-captured-bol__table-action">
                          <button
                            type="button"
                            className="xgs-captured-bol__remove-item"
                            onClick={() => remove(idx)}
                            disabled={values.items.length <= 1}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default BolCommodityRows;
